import { type FormEvent, useState } from "react";

import { adminApi } from "@/lib/adminApi";
import { ActionRow, Field, PanelSection, StatusBanner } from "@/components/ui";
import { useSaveState } from "@/lib/useSaveState";

const MIN_LENGTH = 12;

interface AccountPanelProps {
  readonly username: string;
  readonly onSignedOut: () => void;
}

export function AccountPanel({ username, onSignedOut }: AccountPanelProps) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const save = useSaveState(onSignedOut);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_LENGTH;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (mismatch || tooShort) return;
    const done = await save.run(() => adminApi.changePassword(current, next));
    if (done) {
      // The server ends every session on a password change, including this one.
      setCurrent("");
      setNext("");
      setConfirm("");
      window.setTimeout(onSignedOut, 1200);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <PanelSection
        title="Your account"
        description={`Signed in as ${username}. Changing your password signs you out everywhere.`}
      >
        <div className="flex max-w-[26rem] flex-col gap-4">
          <Field label="Current password" htmlFor="current-password">
            <input
              id="current-password"
              className="input"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>

          <Field
            label="New password"
            htmlFor="new-password"
            hint={`At least ${MIN_LENGTH} characters. A short phrase works well.`}
            error={tooShort ? `Use at least ${MIN_LENGTH} characters.` : undefined}
          >
            <input
              id="new-password"
              className="input"
              type="password"
              autoComplete="new-password"
              required
              aria-invalid={tooShort ? "true" : undefined}
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </Field>

          <Field
            label="Confirm new password"
            htmlFor="confirm-password"
            error={mismatch ? "These do not match." : undefined}
          >
            <input
              id="confirm-password"
              className="input"
              type="password"
              autoComplete="new-password"
              required
              aria-invalid={mismatch ? "true" : undefined}
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </Field>
        </div>

        <p className="mt-5 max-w-[62ch] border-t-2 border-ink pt-4 text-[0.875rem] leading-relaxed opacity-70">
          Adding or removing officers is done on the server, so a compromised
          browser session cannot create new accounts:
          <code className="ml-1 font-mono">python -m tetrisatuci.admin_cli add someone</code>
        </p>
      </PanelSection>

      <StatusBanner tone={save.tone} message={save.message} />

      <ActionRow>
        <button
          type="submit"
          className="btn"
          disabled={save.saving || mismatch || tooShort || !next}
        >
          {save.saving ? "Changing…" : "Change password"}
        </button>
      </ActionRow>
    </form>
  );
}
